/**
 * The offer wizard's ordered steps (TO-125 offer-wizard-send-step REQ-101). Each
 * step is its own route — the draft's id carries the state from Step 2 on — and
 * the ids are the shell header's step keys (`wizard.steps.<id>`). Step 3's page
 * (`/message`) is TO-124's; it navigates with these helpers like the others.
 */
export enum OfferWizardStepId {
	RECIPIENT = 'recipient',
	PRODUCTS = 'products',
	PREVIEW = 'preview',
	SEND = 'send',
}

export interface OfferWizardStep {
	id: OfferWizardStepId
	/** The step's route for the draft `offerId` (Step 1 runs before the draft exists). */
	path: (offerId: string) => string
}

const draftPath = (segment: string) => (offerId: string) => `/offers/${encodeURIComponent(offerId)}/${segment}`

/** The registered steps, in order. */
export const OFFER_WIZARD_STEPS: readonly OfferWizardStep[] = [
	{ id: OfferWizardStepId.RECIPIENT, path: () => '/offers/new' },
	{ id: OfferWizardStepId.PRODUCTS, path: draftPath('products') },
	{ id: OfferWizardStepId.PREVIEW, path: draftPath('message') },
	{ id: OfferWizardStepId.SEND, path: draftPath('send') },
]

/** The step's position in the header (0-based). */
export const stepIndexOf = (id: OfferWizardStepId): number => OFFER_WIZARD_STEPS.findIndex((step) => step.id === id)

/** The step after `id`, or undefined on the last step. */
export const nextStep = (id: OfferWizardStepId): OfferWizardStep | undefined => OFFER_WIZARD_STEPS[stepIndexOf(id) + 1]

/**
 * The step before `id`, or undefined. Step 2 has none: the recipient is fixed once
 * the draft exists, and going back to Step 1 would create another draft.
 */
export const previousStep = (id: OfferWizardStepId): OfferWizardStep | undefined =>
	id === OfferWizardStepId.PRODUCTS ? undefined : OFFER_WIZARD_STEPS[stepIndexOf(id) - 1]
